import React, { ReactNode, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import {
  Box,
  Divider,
  HStack,
  Image,
  Stack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { MenuIcon } from 'lucide-react';

import { AgentButton, PageTitle, SearchInput } from '@/components/common';
import { IconButton } from '@/components/core';
import { GlobalSearchOverlay } from '@/components/features/GlobalSearch';
import { NotificationMenu } from '@/components/features/Notifications';
import { AgentName } from '@/constant/agents';
import { ROUTES } from '@/constant/routes';
import { useAgents } from '@/store/agents';
import { ChatType, useChats } from '@/store/chats';
import { getPageTitle } from '@/utils/pageTitle';

interface HeaderProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (isMobileMenuOpen: boolean) => void;
}

export const Header: React.FC<HeaderProps> = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
}) => {
  const { pathname } = useLocation();
  const updateChatState = useChats(state => state.updateChatState);
  const { data: connie, isLoading: isConnieLoading } = useAgents(
    AgentName.CONNIE
  );

  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');

  const isLarge = useBreakpointValue({ base: false, xl: true });
  const isPhone = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  const isQuoteDocumentDetail = pathname.startsWith(
    `${ROUTES.QUOTE_DOCUMENTS}/`
  );
  const showStackedSearch = !isLarge && !(isPhone && isQuoteDocumentDetail);

  const openGlobalSearch = (query = '') => {
    if (isGlobalSearchOpen) return;
    setGlobalSearchQuery(query);
    setIsGlobalSearchOpen(true);
  };

  const handleSearch = (query: string) => {
    openGlobalSearch(query);
  };

  const handleQueryChange = (query: string) => {
    if (query.trim().length >= 2) {
      openGlobalSearch(query);
    }
  };

  const pageTitle = useMemo(() => getPageTitle(pathname), [pathname]);

  const searchInput: ReactNode = (
    <SearchInput
      enableAutoComplete={false}
      onSearch={handleSearch}
      onQueryChange={handleQueryChange}
      onInputFocus={() => openGlobalSearch(globalSearchQuery)}
      maxWidth={{ base: 'full', xl: '480px' }}
      position={{ base: 'relative', xl: 'absolute' }}
      right={{ base: 'auto', xl: 0 }}
      left={{ base: 'auto', xl: 0 }}
      marginX='auto'
      data-cy='header-global-search'
    />
  );

  return (
    <Stack
      as='header'
      position='relative'
      zIndex={1}
      bg='linear-gradient(180deg, #171A1D 5%, rgba(23, 26, 29, 0.60) 100%)'
      backdropFilter='12px'
      gap={3}
      px={{ base: 4, md: 6, xl: 0 }}
      _after={{
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: '-1px',
        height: '2px',
        bg: 'app-background',
        zIndex: 0,
        pointerEvents: 'none',
        borderBottom: '1px solid',
        borderColor: 'white.50',
      }}
    >
      <HStack
        alignSelf='stretch'
        pt={{ base: 2, xl: 4 }}
        pr={{ base: 0, xl: 6 }}
        pl={{ base: '44px', xl: 6 }}
        pb={{ base: 2, xl: 4 }}
        justifyContent={{ base: 'flex-start', md: 'space-between' }}
        gap={{ base: 2, xl: 6 }}
        w='full'
        borderBottom='1px solid'
        borderColor='white.50'
      >
        <HStack display={{ base: 'none', md: 'flex' }} gap='18px'>
          <IconButton
            data-cy='sidebar-toggle-button'
            display={{ base: 'none', xl: 'flex' }}
            size='xs'
            minWidth={6}
            height={6}
            variant='ghost'
            aria-label='Toggle menu'
            opacity={0.3}
            icon={<MenuIcon size={20} />}
            _hover={{
              bgColor: 'transparent',
              opacity: 1,
            }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          />
          <Image
            src='https://cdn.prod.website-files.com/66f62ac0b4dbc96bb348eb73/694811546c1d9a052e5631f8_VH3AI%20-%20Main%20Logo%20-%20Color.png'
            alt='VH3 AI'
            h='22px'
            w='auto'
            objectFit='contain'
          />
        </HStack>
        {pageTitle && <PageTitle title={pageTitle} />}
        <HStack flex={1} gap={4} justifyContent='flex-end' width='full'>
          {isLarge && searchInput}
        </HStack>
        <AgentButton
          agent={connie}
          isLoading={isConnieLoading}
          loadingText='Ask Connie'
          minW='150px'
          disabled={!connie}
          data-cy='header-ask-connie'
          onClick={() => updateChatState(ChatType.connie, { isOpen: true })}
        />
        <NotificationMenu />
      </HStack>
      {showStackedSearch && (
        <>
          {searchInput}
          <Box pt={{ base: 1, xl: 0 }}>
            <Box position='relative' h='1px'>
              <Box
                position='absolute'
                left={0}
                right={0}
                top={0}
                bottom={0}
                bg='app-background'
              />
              <Divider
                position='absolute'
                inset={0}
                borderColor='white.50'
                opacity={1}
              />
            </Box>
          </Box>
        </>
      )}
      <GlobalSearchOverlay
        isOpen={isGlobalSearchOpen}
        onClose={() => setIsGlobalSearchOpen(false)}
        initialQuery={globalSearchQuery}
      />
    </Stack>
  );
};
